import { AppToastOutlet, useAppToastRef } from './app-toast-provider';
import React, { createContext, useState, useCallback, useMemo, useEffect, useRef, useContext } from "react";
import { Api } from "../general/api";
import { useApi } from "./api-provider";
import { Toast } from "primereact/toast";
import { CssProperty, CUSTOM_THEMES_FOLDER, GetThemesDto, MessageDto, ThemeCategory, ThemeDto, ThemeNode } from "../general/dtos";
import { useBookmarkSelection } from '../general/bookmark-selection';
import { useUnsavedActions, useUnsavedDraft } from '../general/unsaved-changes-context';

export interface ThemesDataContextValue {
    toastRef: React.RefObject<Toast | null>;
    mode: "view" | "edit" | "create";
    setMode: (mode: "view" | "edit" | "create") => void;
    themes: GetThemesDto;
    selectedTheme: ThemeDto | null;
    setSelectedTheme: (theme: ThemeDto | null) => void;
    loading: boolean;
    cssSelectors: ThemeCategory[];
    generatedCss: string;
    refreshThemes: () => Promise<void>;
    loadThemeCss: (fileName: string) => Promise<void>;

    findCssValue: (selector: string, propertyName: string) => string | undefined;
    updateThemeName: (name: string) => void;
    updateCssProperty: (selector: string, oldPropertyName: string, newPropertyName: string, newValue: string) => void;
    addCssProperty: (selector: string, newProp: CssProperty) => void;
    deleteCssProperty: (selector: string, propertyName: string) => void;
    bulkUpdateCssProperties: (selector: string, newProperties: CssProperty[], mode: 'replace' | 'merge') => void;
    removeSelector: (selector: string) => void;
    saveTheme: () => Promise<void>;
    deleteTheme: (value: string) => Promise<void>;
    revertChanges: () => void;
}

interface ThemeSnapshot {
    label: string;
    value: string;
    nodes: ThemeCategory[];
}

export const ThemesDataContext = createContext<ThemesDataContextValue | null>(null);

export type ThemesDataProviderProps = {
    children: React.ReactNode;
};

export function ThemesDataProvider({ children }: ThemesDataProviderProps) {
    const toastRef = useAppToastRef();
    const { withoutWarning, afterSave } = useUnsavedActions();
    const api: Api | null = useApi();

    const [mode, setMode] = useState<"view" | "edit" | "create">("view");
    const [loading, setLoading] = useState<boolean>(false);
    const [themes, setThemes] = useState<GetThemesDto>({ standard: [], custom: [] });
    const [selectedTheme, setSelectedThemeState] = useState<ThemeDto | null>(null);
    const [themeKey, setThemeKey] = useBookmarkSelection('theme');
    const setSelectedTheme = useCallback((theme: ThemeDto | null) => {
        setSelectedThemeState(theme);
        setThemeKey(theme?.value ?? null);
    }, [setThemeKey]);
    const cssLoadGeneration = useRef(0);
    const [cssSelectors, setCssSelectors] = useState<ThemeCategory[]>([]);
    const lastLoadedFile = useRef<string | null>(null);
    const [originalSnapshot, setOriginalSnapshot] = useState<ThemeSnapshot | null>(null);

    const refreshThemes = useCallback(async (): Promise<void> => {
        if (!api) return;
        setLoading(true);
        try {
            const data: GetThemesDto = await api.getThemes();
            setThemes(data);
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: 'Load error',
                detail: 'Could not load theme lists.'
            });
        } finally {
            setLoading(false);
        }
    }, [api, toastRef]);

    const loadThemeCss = useCallback(async (fileName: string): Promise<void> => {
        if (lastLoadedFile.current === fileName) {
            return;
        }

        if (!api) {
            return;
        }

        setLoading(true);
        const generation = ++cssLoadGeneration.current;
        try {
            const data: ThemeCategory[] = await api.getThemeCss(fileName);
            if (generation !== cssLoadGeneration.current) return;
            setCssSelectors(data);
            guard.markSaved({ theme: selectedTheme, nodes: data });
            setOriginalSnapshot({
                label: selectedTheme?.label || '',
                value: selectedTheme?.value || '',
                nodes: data
            });
            lastLoadedFile.current = fileName;
        } catch (error) {
            if (generation !== cssLoadGeneration.current) return;
            toastRef.current?.show({
                severity: 'error',
                summary: 'CSS load error',
                detail: 'Failed to fetch theme styles.'
            });
        } finally {
            if (generation === cssLoadGeneration.current) setLoading(false);
        }
    }, [api, selectedTheme?.label, selectedTheme?.value, toastRef]);

    const saveTheme = async (): Promise<void> => {
        if (!api || !selectedTheme) {
            return;
        }

        const flatNodes: ThemeNode[] = cssSelectors.flatMap(
            (category: ThemeCategory) => category.children
        );

        try {
            let newValue: string = '';
            if (mode === 'create') {
                const originalName: string = selectedTheme.value;
                newValue = `${CUSTOM_THEMES_FOLDER}/${selectedTheme.label}`;
                const result: MessageDto | null = await api.createTheme(newValue, flatNodes, originalName); // set folder name to be equal to the theme name under _custom folder
                if (result?.message) {
                    throw new Error(result?.message);
                }
                selectedTheme.value = newValue;
                themes.custom.push(selectedTheme);
                setThemes({ standard: themes.standard, custom: themes.custom });
            } else {
                const originalValue: string = originalSnapshot?.value ?? selectedTheme.value;
                newValue = originalValue.replace(`/${originalSnapshot?.label}`, `/${selectedTheme.label}`);
                const result: MessageDto | null = await api.updateTheme(originalValue, newValue, flatNodes);
                if (result?.message) {
                    throw new Error(result?.message);
                }
            }

            setOriginalSnapshot({
                label: selectedTheme.label,
                value: newValue,
                nodes: cssSelectors
            });
            guard.markSaved({ theme: { ...selectedTheme, value: newValue }, nodes: cssSelectors });
            setSelectedThemeState({ ...selectedTheme, value: newValue });
            afterSave(() => setThemeKey(newValue, true));
            lastLoadedFile.current = newValue;
            setMode('view');

            toastRef.current?.show({
                severity: 'success',
                summary: 'Theme saved',
                detail: 'Your changes have been persisted.'
            });
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: 'Save error',
                detail: 'Failed to persist theme changes.'
            });
        }
    };

    const deleteTheme = async (value: string): Promise<void> => {
        if (!api || !selectedTheme) {
            return;
        }
        try {
            const result: MessageDto | null = await api.deleteTheme(value);
            if (result?.message) {
                throw new Error(result?.message);
            }
            setThemes({ standard: themes.standard, custom: themes.custom.filter(t => t.value !== value) });
            setSelectedTheme(null);
            setOriginalSnapshot(null);

            toastRef.current?.show({
                severity: 'success',
                summary: 'Theme deleted',
                detail: 'Your changes have been persisted.'
            });
        } catch (error) {
            toastRef.current?.show({
                severity: 'error',
                summary: 'Delete error',
                detail: 'Failed to delete theme.'
            });
        }
    }

    const revertChanges = (): void => {
        if (!originalSnapshot) {
            return;
        }

        if (selectedTheme) {
            setSelectedTheme({
                ...selectedTheme,
                label: originalSnapshot.label
            });
        }
        setCssSelectors(originalSnapshot.nodes);
        setMode('view');
    };

    const generatedCss = useMemo(() => {
        if (!Array.isArray(cssSelectors)) return "";

        const rootContent: string[] = [];
        const tagContent: string[] = [];
        const layerContent: string[] = [];

        // 1. Pre-extract all import nodes BEFORE recursive processing
        const allFlatNodes = cssSelectors.flatMap(cat => cat.children);
        const importNodes = allFlatNodes.filter(n => n.selector.startsWith('__import_'));

        const importLines = importNodes
            .map(node => node.properties.find(p => p.property === 'url')?.value)
            .filter(Boolean)
            .map(url => `@import url('${url}');`)
            .join('\n');

        // 2. Existing processing logic
        const processNodes = (nodes: ThemeNode[]) => {
            for (const node of nodes) {
                // Skip the import nodes so they don't get processed as regular CSS blocks
                if (node.selector.startsWith('__import_')) continue;

                const selector = node.selector.startsWith('__font-face-') ? '@font-face' : node.selector;

                const props = node.properties.map(p => `  ${p.property}: ${p.value};`).join('\n');
                const cssBlock = `${selector} {\n${props}\n}`;

                if (selector === '@font-face' || selector === ':root' || selector.startsWith('.p-editor-container')) {
                    rootContent.push(cssBlock);
                } else if (/^[a-z0-9]+$/i.test(selector)) {
                    tagContent.push(cssBlock);
                } else {
                    layerContent.push(`  ${cssBlock}`);
                }

                if (node.children?.length > 0) processNodes(node.children);
            }
        };

        cssSelectors.forEach(cat => processNodes(cat.children));

        // 3. Assemble: Imports first, then the rest
        const mainCss: string = [
            rootContent.join('\n\n'),
            tagContent.join('\n\n'),
            layerContent.length > 0 ? `@layer primereact {\n${layerContent.join('\n\n')}\n}` : ''
        ].filter(Boolean).join('\n\n');

        const themeBase: string = `/themes/${selectedTheme?.value}/`;
        const optimizedCss: string = mainCss.replace(/(@font-face\s*{[^}]*src:\s*url\(['"]?)\.\//g, `$1${themeBase}`);

        return importLines ? `${importLines}\n\n${optimizedCss}` : optimizedCss;

    }, [cssSelectors, selectedTheme?.value]);

    useEffect(() => {
        if (api) {
            refreshThemes();
        }
    }, [api, refreshThemes]);

    useEffect(() => {
        if (themeKey === selectedTheme?.value) return;
        cssLoadGeneration.current++;
        lastLoadedFile.current = null;
        setMode('view');
        setOriginalSnapshot(null);
        setCssSelectors([]);
        setSelectedThemeState([...themes.standard, ...themes.custom].find(theme => theme.value === themeKey) ?? null);
    }, [themeKey, themes, selectedTheme?.value]);

    useEffect(() => {
        if (selectedTheme?.value) {
            loadThemeCss(selectedTheme.value);
        } else {
            setCssSelectors([]);
        }
    }, [selectedTheme, loadThemeCss]);

    const findCssValue = useCallback((selector: string, propertyName: string): string | undefined => {
        const searchNodes = (nodes: ThemeNode[]): string | undefined => {
            for (const node of nodes) {
                if (node.selector === selector) {
                    const prop = node.properties.find(p => p.property === propertyName);
                    if (prop) return prop.value;
                }
                if (node.children?.length > 0) {
                    const found = searchNodes(node.children);
                    if (found) return found;
                }
            }
            return undefined;
        };

        for (const cat of cssSelectors) {
            const found = searchNodes(cat.children);
            if (found) return found;
        }
        return undefined;
    }, [cssSelectors]);

    const updateThemeName = (newName: string): void => {
        if (selectedTheme) {
            setSelectedTheme({
                ...selectedTheme,
                label: newName
            });
        }
    };

    const updateCssProperty = (selector: string, oldPropertyName: string, newPropertyName: string, newValue: string): void => {
        setCssSelectors(prev => prev.map(category => ({
            ...category,
            children: updateNodes(category.children, selector, oldPropertyName, newPropertyName, newValue)
        })));
    };

    const updateNodes = (nodes: ThemeNode[], selector: string, oldPropName: string, newPropName: string, newVal: string): ThemeNode[] => {
        return nodes.map((node) => {
            // 1. If this node matches the selector, perform the update on its properties
            if (node.selector === selector) {
                return {
                    ...node,
                    properties: node.properties.map((p) =>
                        p.property === oldPropName
                            ? { ...p, property: newPropName, value: newVal }
                            : p
                    ),
                };
            }

            // 2. If this node has children, recurse into them to continue the search
            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: updateNodes(node.children, selector, oldPropName, newPropName, newVal),
                };
            }

            // 3. Return node unchanged if it does not match and has no children
            return node;
        });
    };

    const addCssProperty = (selector: string, newProp: CssProperty): void => {
        setCssSelectors(prev => prev.map(category => ({
            ...category,
            children: addNodes(category.children, selector, newProp)
        })));
    };

    const addNodes = (nodes: ThemeNode[], selector: string, newProp: CssProperty): ThemeNode[] => {
        return nodes.map(node => {
            if (node.selector === selector) {
                // Return a NEW node object with a NEW properties array
                return {
                    ...node,
                    properties: [...node.properties, newProp]
                };
            }
            // If children exist, recurse and maintain immutability
            if (node.children?.length) {
                return {
                    ...node,
                    children: addNodes(node.children, selector, newProp)
                };
            }
            return node;
        });
    };

    const deleteCssProperty = (selector: string, propertyName: string): void => {
        setCssSelectors(prev => prev.map(category => ({
            ...category,
            children: deleteNodes(category.children, selector, propertyName)
        })));
    };

    const bulkUpdateCssProperties = (
        selector: string,
        newProperties: CssProperty[],
        mode: 'replace' | 'merge'
    ): void => {
        setCssSelectors(prev => {
            // CHECK IF THERE ARE MODIFICATIONS - begin
            /*let existingNode: ThemeNode | null = null;

            const findNode = (nodes: ThemeNode[]): ThemeNode | null => {
                for (const node of nodes) {
                    if (node.selector === selector) return node;
                    if (node.children?.length > 0) {
                        const found = findNode(node.children);
                        if (found) return found;
                    }
                }
                return null;
            };

            for (const cat of prev) {
                const found = findNode(cat.children);
                if (found) { existingNode = found; break; }
            }

            if (existingNode) {
                const isMatch = mode === 'replace'
                    ? JSON.stringify(existingNode.properties) === JSON.stringify(newProperties)
                    : newProperties.every(newP => {
                        const oldP = existingNode!.properties.find(p => p.property === newP.property);
                        return oldP && oldP.value === newP.value;
                    });

                if (isMatch) return prev; // Return 'prev' to bail out of the re-render
            }*/
            // CHECK IF THERE ARE MODIFICATIONS - end

            let found = false;

            const updateNodes = (nodes: ThemeNode[]): ThemeNode[] => {
                return nodes.map(node => {
                    if (node.selector === selector) {
                        found = true;

                        if (mode === 'replace') {
                            // Original behavior for the Data Table: Full replacement
                            return { ...node, properties: [...newProperties] };
                        } else {
                            // Merge strategy for the Dialog: Partial update
                            const updatedProps = [...node.properties];
                            newProperties.forEach(newP => {
                                const index = updatedProps.findIndex(p => p.property === newP.property);
                                if (index !== -1) {
                                    updatedProps[index] = newP; // Update existing
                                } else {
                                    updatedProps.push(newP); // Append new
                                }
                            });
                            return { ...node, properties: updatedProps };
                        }
                    }

                    if (node.children?.length > 0) {
                        return { ...node, children: updateNodes(node.children) };
                    }
                    return node;
                });
            };

            const updated = prev.map(category => ({
                ...category,
                children: updateNodes(category.children)
            }));

            // Handle missing nodes
            if (!found) {
                const newNode: ThemeNode = {
                    selector,
                    properties: newProperties,
                    children: []
                };
                const hiddenCatIndex = updated.findIndex(c => c.title === "__hidden");
                if (hiddenCatIndex !== -1) {
                    updated[hiddenCatIndex].children.push(newNode);
                } else {
                    updated.push({ title: "__hidden", children: [newNode] });
                }
            }
            return updated;
        });
    };

    const removeSelector = (selector: string): void => {
        setCssSelectors(prev => {
            const removeFromNodes = (nodes: ThemeNode[]): ThemeNode[] => {
                return nodes
                    .filter(node => node.selector !== selector) // Remove the node
                    .map(node => ({
                        ...node,
                        children: node.children ? removeFromNodes(node.children) : []
                    }));
            };
            return prev.map(category => ({
                ...category,
                children: removeFromNodes(category.children)
            }));
        });
    };

    const deleteNodes = (nodes: ThemeNode[], selector: string, propName: string): ThemeNode[] => {
        return nodes.map(node => {
            if (node.selector === selector) {
                return {
                    ...node,
                    properties: node.properties.filter(p => p.property !== propName)
                };
            }
            if (node.children?.length > 0) {
                return { ...node, children: deleteNodes(node.children, selector, propName) };
            }
            return node;
        });
    };

    const guard = useUnsavedDraft({ active: mode !== 'view' && selectedTheme !== null,
        value: { theme: selectedTheme, nodes: cssSelectors }, parameter: 'theme',
        save: saveTheme, discard: revertChanges });

    return (
        <ThemesDataContext.Provider
            value={{
                toastRef,
                mode,
                setMode,
                themes,
                selectedTheme,
                setSelectedTheme,
                loading,
                cssSelectors,
                generatedCss,
                refreshThemes,
                loadThemeCss,

                findCssValue,
                updateThemeName,
                updateCssProperty,
                addCssProperty,
                deleteCssProperty,
                bulkUpdateCssProperties,
                removeSelector,
                saveTheme,
                deleteTheme,
                revertChanges: () => withoutWarning(revertChanges),
            }}
        >
            {children}

        <AppToastOutlet toastRef={toastRef} />
    </ThemesDataContext.Provider>
    );
}

export const useThemesData = (): ThemesDataContextValue => {
    const context = useContext(ThemesDataContext);
    if (!context) throw new Error("useThemesData must be used within a ThemesDataProvider");
    return context;
};
